import { useEffect, useState } from "react";
import "./App.css";

import { Route, useNavigate } from "react-router-dom";
import {
  fetchAuthTokenSSOAction,
  setLoggedOut,
} from "./store/actions";
import { getSSOUserSelector } from "./store/selectors";

import { getEnviornmentConfig, isLocalHost } from "./../src/util/helpers";
import { BrowsePage } from "./components/browse-page/browse-page";
import {
  GSKReactApp,
  IGSKAppProps,
} from "./components/gsk-main/gsk-react-app/gsk-react-app";
import { MenuItemType } from "./components/upbar/sub-components/selected-page-container/selected-page-container";
import { SearchPage } from "./components/search-page/search-page";
import { UploadPage } from "./components/upload-page/upload-page";
import { PreferencesPage } from "./components/settings/preferences";
import { ConfigProvider } from "antd";
import { DownloadPage } from "./components/download-cli-page/download-page";
import { AppInsightsContext, ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

console.log(process.env.REACT_APP_DESCRIPTION);

console.log(process.env.REACT_APP_TITLE);

const reactPlugin = new ReactPlugin();
if (!isLocalHost()) {
  const appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: getEnviornmentConfig().instrumentationKey,
      enableAutoRouteTracking: true,
      extensions: [reactPlugin],
    },
  });
  appInsights.loadAppInsights();
}

export const pageSelectorContainer: MenuItemType[] = [
  {
    key: "browse-page",
    label: "Browse",
    path: "browse",
    className: "menu-item",
  },
  {
    key: "search-page",
    label: "Search",
    path: "search",
    className: "menu-item",
  },
  {
    key: "upload-page",
    label: "Upload",
    path: "upload",
    className: "menu-item",
  },
];

export const userDropdownSelector: MenuItemType[] = [
  // { key: "profile-page", label: "Profile", path: "profile" },
  { key: "preferences-page", label: "Preferences", path: "preferences" },
  { key: "logout-page", label: "Logout", path: "logout" },
];

export interface UserBrowsingSessionProps {
  theme: string;
  containerState: string | null;
  updateContainerState: (newContainer: string) => void;
}

export default function App() {
  const prevContainerState = localStorage.getItem("containerState");
  const [containerState, setContainerState] = useState<string | null>(
    prevContainerState
  );

  const updateContainerState = (newContainer: string) => {
    setContainerState(newContainer);
    localStorage.setItem("containerState", newContainer);
  };

  const prevSelectedTheme = localStorage.getItem("theme");

  type themes = "light" | "dark";

  const defaultLight = window.matchMedia(
    "(prefers-color-scheme: light)"
  ).matches;
  const [theme, setTheme] = useState<themes>(defaultLight ? "light" : "dark");
  const [isDefaultChecked, setIsDefaultChecked] = useState<boolean>(true);

  const updateTheme = (newTheme: themes) => {
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  function switchTheme(): void {
    const newTheme = theme === "light" ? "dark" : "light";
    updateTheme(newTheme);
  }

  const sessionProps: UserBrowsingSessionProps = {
    theme,
    containerState,
    updateContainerState,
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (
      (prevSelectedTheme === "light" || prevSelectedTheme === "dark") &&
      prevSelectedTheme !== theme
    ) {
      setTheme(prevSelectedTheme);
    }
    if (window.location.pathname === "/") {
      navigate("/browse", { replace: true });
    }
  }, []);

  useEffect(() => {
    if (theme === "light") {
      setIsDefaultChecked(false);
    } else {
      setIsDefaultChecked(true);
    }
  }, [theme]);

  const bgColor = theme === "light" ? "#ffffff" : "#000000";
  const textColor = theme === "light" ? "#000000" : "#ffffff";
  const placeholderText = "#959595";

  const props: IGSKAppProps = {
    singleSignOn: {
      action: fetchAuthTokenSSOAction,
      authProviderProps: {
        authUrl: getEnviornmentConfig().authUrl,
        children: <div></div>,
        clientId: getEnviornmentConfig().clientId,
        homePageUrl: "/",
        loadingScreen: (
          <div className="loadingScreen">
            <img src="logo192.png" alt="GSK Logo" />
            <br />
            <span>Loading...</span>
          </div>
        ),
        redirectUrl: getEnviornmentConfig().clientUrl,
        refreshTokensEnabled: true,
      },
      isSingleSignOnEnabled: true,
      selector: getSSOUserSelector,
    },
    ui: {
      headerMenu: {
        description: "web " + window._env_.REACT_APP_VERSION,
        loggedOutaction: setLoggedOut,
        title: "R&D Data Storage",
      },
      routes: [
        <Route
          key="browse-page"
          path="/browse"
          element={<BrowsePage {...sessionProps} />}
        ></Route>,
        <Route
          key="search-page"
          path="/search"
          element={<SearchPage {...sessionProps} />}
        ></Route>,
        <Route
          key="upload-page"
          path="/upload"
          element={<UploadPage {...sessionProps} />}
        ></Route>,
        <Route
          key="preferences-page"
          path="/preferences"
          element={
            <PreferencesPage
              switchTheme={switchTheme}
              isChecked={isDefaultChecked}
            />
          }
        ></Route>,
        <Route
          key="download-page"
          path="/download"
          element={<DownloadPage />}
        ></Route>,
      ],
      showTerminal: false,
      selectedPageContainer: { items: pageSelectorContainer },
      sideMenu: { items: userDropdownSelector },
      theme: theme,
    },
  };

  return (
    <div data-theme={theme}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <ConfigProvider
          theme={{
            components: {
              Table: {
                colorText: textColor,
                colorBgContainer: bgColor,
                colorTextHeading: textColor,
              },
              Modal: {
                contentBg: bgColor,
                headerBg: bgColor,
                colorText: textColor,
                colorTextHeading: textColor,
              },
              Input: {
                colorText: textColor,
                colorPrimaryText: textColor,
                colorTextPlaceholder: placeholderText,
              },
            },
          }}
        >
          <GSKReactApp {...props} />
        </ConfigProvider>
      </AppInsightsContext.Provider>
    </div>
  );
}
